import { useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";

import { refusalText } from "../api/errors";
import { useApiCommand } from "../api/hooks";
import type { QueryName } from "../api/routes";
import type { ItemPart, ItemParts } from "../api/types";
import { Button } from "../ui/Button";
import { Dialog } from "../ui/Dialog";
import { Pill } from "../ui/Pill";
import { ITEM_PART_WORDS, PART_STATUS_WORDS } from "../ui/words";

/** What a mark on a part makes stale: the strip itself and every screen that shows it. */
const STALE: readonly QueryName[] = ["item_parts", "item_snapshot", "item_page", "session"];

interface PartsStripProps {
  parts: ItemParts;
  /** The item the strip is about (needed to mark a part "none needed"). */
  itemId: string;
  /** The person may mark parts. Off for someone else's session, and for a reader who is not an engineer. */
  editable?: boolean;
}

/**
 * The item parts strip: each part of the item with one word, filled, waiting for the SME, not started or none needed
 * (architecture 11). "Not started" is a note, never a block. "None needed" is the engineer's mark, with a reason.
 * One strip for the session, the item page and the hand-over, so the three say the same words (UI-25).
 */
export function PartsStrip({ parts, itemId, editable = false }: PartsStripProps) {
  const queryClient = useQueryClient();
  const [marking, setMarking] = useState<ItemPart | null>(null);
  const [reason, setReason] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const setMark = useApiCommand("set_part_mark");
  const clearMark = useApiCommand("clear_part_mark");
  const refresh = () => {
    for (const name of STALE) void queryClient.invalidateQueries({ queryKey: [name] });
  };

  const close = () => {
    setMarking(null);
    setReason("");
    setProblem(null);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!marking || !reason.trim()) return;
    setProblem(null);
    try {
      await setMark.mutateAsync({ id: itemId, part: marking, reason: reason.trim() });
      refresh();
      close();
    } catch (error) {
      setProblem(refusalText(error));
    }
  };

  const clear = async (part: ItemPart) => {
    setProblem(null);
    try {
      await clearMark.mutateAsync({ id: itemId, part });
      refresh();
    } catch (error) {
      setProblem(refusalText(error));
    }
  };

  return (
    <section className="parts-strip" aria-label="What is filled in this item">
      <h3 className="parts-strip__title">What is filled in</h3>
      <ul className="parts-strip__list">
        {parts.parts.map((entry) => (
          <li key={entry.part} className="parts-strip__part" data-word={entry.word}>
            <span className="parts-strip__name">{ITEM_PART_WORDS[entry.part] ?? entry.part}</span>
            <Pill tone={entry.word === "waiting_for_sme" ? "waiting" : "neutral"}>
              {PART_STATUS_WORDS[entry.word] ?? entry.word}
              {entry.count > 0 ? `, ${entry.count}` : ""}
            </Pill>
            {entry.word === "none_needed" && entry.reason && <span className="muted parts-strip__reason">{entry.reason}</span>}
            {editable && entry.word === "not_started" && (
              <Button variant="quiet" onClick={() => setMarking(entry.part)} aria-label={`Mark ${ITEM_PART_WORDS[entry.part]} as none needed`}>
                None needed
              </Button>
            )}
            {editable && entry.word === "none_needed" && (
              <Button variant="quiet" busy={clearMark.isPending} onClick={() => void clear(entry.part)} aria-label={`Take the mark off ${ITEM_PART_WORDS[entry.part]}`}>
                Take the mark off
              </Button>
            )}
          </li>
        ))}
      </ul>
      {!marking && problem && (
        <p role="alert" className="form-error">
          {problem}
        </p>
      )}
      <Dialog
        closeOnBackdrop={false}
        open={marking !== null}
        onClose={close}
        title={marking ? `${ITEM_PART_WORDS[marking]}: none needed` : "None needed"}
        size="small"
        footer={
          <>
            <Button onClick={close}>Cancel</Button>
            <Button variant="primary" type="submit" form="part-mark-form" busy={setMark.isPending} disabled={!reason.trim()}>
              Mark as none needed
            </Button>
          </>
        }
      >
        <form id="part-mark-form" onSubmit={(event) => void submit(event)} className="stack">
          <label className="field">
            <span className="field__label">Why does this item need none?</span>
            <textarea className="field__input" rows={3} value={reason} onChange={(event) => setReason(event.target.value)} data-autofocus />
          </label>
          {problem && (
            <p role="alert" className="form-error">
              {problem}
            </p>
          )}
        </form>
      </Dialog>
    </section>
  );
}
